import {
  action,
  computed,
  makeObservable,
  observable,
  reaction,
  runInAction,
} from 'mobx';
import { transport } from '@/core';
import { storageService } from '@/shared/storage/storage.service';

const FORCED_STATUS_STORAGE_KEY = 'RCDPW.forceEnableCoveoSearch';
const FORCED_USE_RING_SEARCH_STORAGE_KEY = 'RCDPW.forceUseRingSearch';
const RING_SEARCH_BASE_URL_STORAGE_KEY = 'RCAPW.ringSearchBaseUrl';
const RING_SEARCH_APP_ID_STORAGE_KEY = 'RCAPW.ringSearchAppId';
const FORCE_ENABLE = String(true);
const FORCE_DISABLE = String(false);

export class ExternalSearchStore {
  @computed
  public get isEnabled(): boolean {
    if (this.forcedStatus !== undefined) {
      return this.forcedStatus;
    }

    return this.remoteParameterStatus;
  }

  @computed
  public get useRingSearch(): boolean {
    if (this.forcedUseRingSearch !== undefined) {
      return this.forcedUseRingSearch;
    }

    return this.remoteUseRingSearch;
  }

  @observable
  private remoteParameterStatus: boolean;

  @observable
  private forcedStatus?: boolean;

  @observable
  private remoteUseRingSearch = false;

  @observable
  private forcedUseRingSearch?: boolean;

  @observable
  public loading = false;

  constructor() {
    makeObservable(this);

    this.resolveRemoteParameterStatus();
    this.resolveRingSearchConfig();
    this.initForcedStatus();
    this.initForcedUseRingSearch();
  }

  @action.bound
  private resolveRemoteParameterStatus(): void {
    this.loading = true;

    // Wait until `transport` is available, as a workaround for circular dependency
    setTimeout(() => {
      const coveoSearchFlagRequest = transport
        .requestCoveoSearchFlag()
        .catch(() => undefined);
      const ringSearchEnabledRequest = transport
        .requestRingSearchEnabled()
        .catch(() => undefined);

      Promise.all([coveoSearchFlagRequest, ringSearchEnabledRequest])
        .then(([coveoRes, ringSearchRes]) => {
          const isEnabled = Boolean(coveoRes?.result);
          const useRingSearch = ringSearchRes?.result === true;

          runInAction(() => {
            this.remoteParameterStatus = isEnabled;
            this.remoteUseRingSearch = useRingSearch;
          });
        })
        .finally(() => {
          runInAction(() => {
            this.loading = false;
          });
        });
    });
  }

  private initForcedStatus(): void {
    this.resolveForcedStatus();
    this.onForcedStatusChange();
    RCAPW.forceEnableCoveoSearch = this.forceEnable;
    RCAPW.clearForcedStatusForCoveoSearch = this.clearForcedStatus;
  }

  @action.bound
  private resolveForcedStatus(): void {
    const forcedStatus = storageService.getItem(FORCED_STATUS_STORAGE_KEY);

    if (forcedStatus === FORCE_ENABLE) {
      this.forceEnable(true);
      return;
    }

    if (forcedStatus === FORCE_DISABLE) {
      this.forceEnable(false);
      return;
    }

    this.clearForcedStatus();
  }

  @action.bound
  private forceEnable(enable = true): void {
    this.forcedStatus = enable;
  }

  @action.bound
  private clearForcedStatus(): void {
    this.forcedStatus = undefined;
  }

  private onForcedStatusChange(): void {
    reaction(
      () => this.forcedStatus,
      (forcedStatus) => {
        if (forcedStatus === undefined) {
          storageService.removeItem(FORCED_STATUS_STORAGE_KEY);
          return;
        }

        storageService.setItem(FORCED_STATUS_STORAGE_KEY, forcedStatus);
      },
    );
  }

  private initForcedUseRingSearch(): void {
    this.resolveForcedUseRingSearch();
    this.onForcedUseRingSearchChange();
    RCAPW.forceUseRingSearch = this.forceUseRingSearch;
    RCAPW.clearForcedUseRingSearch = this.clearForcedUseRingSearch;
  }

  private resolveRingSearchConfig(): void {
    const ringSearchBaseUrl = storageService.getItem(
      RING_SEARCH_BASE_URL_STORAGE_KEY,
    );
    const ringSearchAppId = storageService.getItem(
      RING_SEARCH_APP_ID_STORAGE_KEY,
    );

    if (ringSearchBaseUrl !== null) {
      RCAPW.ringSearchBaseUrl = ringSearchBaseUrl;
    }

    if (ringSearchAppId !== null) {
      RCAPW.ringSearchAppId = ringSearchAppId;
    }
  }

  @action.bound
  private resolveForcedUseRingSearch(): void {
    const forced = storageService.getItem(FORCED_USE_RING_SEARCH_STORAGE_KEY);

    if (forced === FORCE_ENABLE) {
      this.forceUseRingSearch(true);
      return;
    }

    if (forced === FORCE_DISABLE) {
      this.forceUseRingSearch(false);
      return;
    }

    this.clearForcedUseRingSearch();
  }

  @action.bound
  private forceUseRingSearch(enable = true): void {
    this.forcedUseRingSearch = enable;
  }

  @action.bound
  private clearForcedUseRingSearch(): void {
    this.forcedUseRingSearch = undefined;
  }

  private onForcedUseRingSearchChange(): void {
    reaction(
      () => this.forcedUseRingSearch,
      (forced) => {
        if (forced === undefined) {
          storageService.removeItem(FORCED_USE_RING_SEARCH_STORAGE_KEY);
          return;
        }

        storageService.setItem(FORCED_USE_RING_SEARCH_STORAGE_KEY, forced);
      },
    );
  }
}
